import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface EmotioPsychoRationalData {
  responses: AssessmentResponse[];
  scores: {
    overall: number;
    category1: number;
    category2: number;
    category3: number;
    category4: number;
    subscales?: {
      depression: number;
      anxiety: number;
      stress: number;
    };
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
}

const EmotioPsychoRationalAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assessmentFilter, setAssessmentFilter] = useState<string>('none');

  // Auto-select DASS-21 if coming from wellness cards
  useEffect(() => {
    if (location.state?.dass21 && selectedCategory === null) {
      setSelectedCategory(1); // DASS-21 is category 1
      setCurrentQuestion(1);
    }
  }, [location.state, selectedCategory]);

  // Fetch user subscription filter
  useEffect(() => {
    const fetchAssessmentFilter = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION}/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const result = await response.json();
          const accessArr = result.data?.subscription?.plan?.metadata?.access?.userNav || [];
          const filter = result.data?.subscription?.plan?.metadata?.assessmentFilter || 'none';
          if (accessArr.includes('learn')) {
            setAssessmentFilter(filter);
            if (filter === 'dass21' && selectedCategory === null) {
              setSelectedCategory(1); // Auto-select DASS-21
              setCurrentQuestion(1); // Start with first DASS question
            }
          } else {
            setAssessmentFilter('none');
          }
        } else {
          setAssessmentFilter('none');
        }
      } catch {
        setAssessmentFilter('none');
      }
    };
    fetchAssessmentFilter();
  }, [selectedCategory]);

  // Calculate total questions based on selected category
  const getTotalQuestions = () => {
    if (!selectedCategory) {
      return 1; // Just the category selection question
    }
    if (selectedCategory === 1) {
      return 21; // DASS-21 questions
    }
    if (selectedCategory === 2) {
      return 9; // PHQ-9 questions
    }
    if (selectedCategory === 3) {
      return 7; // GAD-7 questions
    }
    if (selectedCategory === 4) {
      return 4; // Gender Sensitivity Questionnaire questions
    }
    return 5; // Other categories have 5 questions each
  };
  
  const totalQuestions = getTotalQuestions();

  // Category selection question
  const categorySelectionQuestion = {
    id: '0',
    question: assessmentFilter === 'dass21' 
      ? 'DASS-21 Assessment (Depression, Anxiety, Stress Scales)' 
      : 'Which Emotio-Psycho-Rational assessment would you like to take?',
    type: 'selection' as const,
    options: assessmentFilter === 'dass21' 
      ? ['DASS-21 (Depression, Anxiety, Stress Scales)']
      : [
          'DASS-21 (Depression, Anxiety, Stress Scales)',
          'PHQ-9 (Patient Wellness Questionnaire)',
          'GAD-7 (Generalized Anxiety Disorder 7-item)',
          'Gender Sensitivity Questionnaire'
        ]
  };

  // DASS level mapping based on QUESTIONNAIRE_REFERENCE.md
  const dassLevelMapping = {
    1: 'DEPRESSION',  // Questions 1-5
    2: 'DEPRESSION',
    3: 'DEPRESSION',
    4: 'DEPRESSION',
    5: 'DEPRESSION',
    6: 'Level 2',  // Questions 6-10
    7: 'Level 2',
    8: 'Level 2',
    9: 'Level 2',
    10: 'Level 2',
    11: 'Level 3', // Questions 11-15
    12: 'Level 3',
    13: 'Level 3',
    14: 'Level 3',
    15: 'Level 3',
    16: 'Level 4', // Questions 16-21
    17: 'Level 4',
    18: 'Level 4',
    19: 'Level 4',
    20: 'Level 4',
    21: 'Level 4'
  };

  // Helper function to get DASS level for current question
  const getDassLevel = (questionId: string, selectedCategory: number | null): string | null => {
    if (selectedCategory !== 1) return null; // Only show for DASS (category 1)
    const questionNumber = parseInt(questionId);
    return dassLevelMapping[questionNumber as keyof typeof dassLevelMapping] || null;
  };

  // Assessment questions organized by category
  const categoryQuestions = {
    1: [
      // DEPRESSION (Items 1-7)
      {
        id: '1',
        question: 'I couldn\'t seem to experience any positive feeling at all.',
        category: 1,
        type: 'likert',
        subscale: 'depression'
      },
      {
        id: '2',
        question: 'I found it difficult to work up the initiative to do things.',
        category: 1,
        type: 'likert',
        subscale: 'depression'
      },
      {
        id: '3',
        question: 'I felt that I had nothing to look forward to.',
        category: 1,
        type: 'likert',
        subscale: 'depression'
      },
      {
        id: '4',
        question: 'I felt down-hearted and blue.',
        category: 1,
        type: 'likert',
        subscale: 'depression'
      },
      {
        id: '5',
        question: 'I was unable to become enthusiastic about anything.',
        category: 1,
        type: 'likert',
        subscale: 'depression'
      },
      {
        id: '6',
        question: 'I felt I wasn\'t worth much as a person.',
        category: 1,
        type: 'likert',
        subscale: 'depression'
      },
      {
        id: '7',
        question: 'I felt that life was meaningless.',
        category: 1,
        type: 'likert',
        subscale: 'depression'
      },
      // ANXIETY (Items 8-14)
      {
        id: '8',
        question: 'I was aware of dryness of my mouth.',
        category: 1,
        type: 'likert',
        subscale: 'anxiety'
      },
      {
        id: '9',
        question: 'I experienced breathing difficulty (e.g. excessively rapid breathing, breathlessness in the absence of physical exertion).',
        category: 1,
        type: 'likert',
        subscale: 'anxiety'
      },
      {
        id: '10',
        question: 'I experienced trembling (e.g., in the hands).',
        category: 1,
        type: 'likert',
        subscale: 'anxiety'
      },
      {
        id: '11',
        question: 'I was worried about situations in which I might panic and make a fool of myself.',
        category: 1,
        type: 'likert',
        subscale: 'anxiety'
      },
      {
        id: '12',
        question: 'I felt I was close to panic.',
        category: 1,
        type: 'likert',
        subscale: 'anxiety'
      },
      {
        id: '13',
        question: 'I was aware of the action of my heart in the absence of physical exertion (e.g. sense of heart rate increase, heart missing a beat).',
        category: 1,
        type: 'likert',
        subscale: 'anxiety'
      },
      {
        id: '14',
        question: 'I felt scared without any good reason.',
        category: 1,
        type: 'likert',
        subscale: 'anxiety'
      },
      // STRESS (Items 15-21)
      {
        id: '15',
        question: 'I found it hard to wind down.',
        category: 1,
        type: 'likert',
        subscale: 'stress'
      },
      {
        id: '16',
        question: 'I tended to over-react to situations.',
        category: 1,
        type: 'likert',
        subscale: 'stress'
      },
      {
        id: '17',
        question: 'I felt that I was using a lot of nervous energy.',
        category: 1,
        type: 'likert',
        subscale: 'stress'
      },
      {
        id: '18',
        question: 'I found myself getting agitated.',
        category: 1,
        type: 'likert',
        subscale: 'stress'
      },
      {
        id: '19',
        question: 'I found it difficult to relax.',
        category: 1,
        type: 'likert',
        subscale: 'stress'
      },
      {
        id: '20',
        question: 'I was intolerant of anything that kept me from getting on with what I was doing.',
        category: 1,
        type: 'likert',
        subscale: 'stress'
      },
      {
        id: '21',
        question: 'I felt that I was rather touchy.',
        category: 1,
        type: 'likert',
        subscale: 'stress'
      }
    ],
    2: [
      {
        id: '1',
        question: 'Little interest or pleasure in doing things.',
        category: 2,
        type: 'likert'
      },
      {
        id: '2',
        question: 'Feeling down, depressed, or hopeless.',
        category: 2,
        type: 'likert'
      },
      {
        id: '3',
        question: 'Trouble falling or staying asleep, or sleeping too much.',
        category: 2,
        type: 'likert'
      },
      {
        id: '4',
        question: 'Feeling tired or having little energy.',
        category: 2,
        type: 'likert'
      },
      {
        id: '5',
        question: 'Poor appetite or overeating.',
        category: 2,
        type: 'likert'
      },
      {
        id: '6',
        question: 'Feeling bad about yourself – or that you are a failure or have let yourself or your family down.',
        category: 2,
        type: 'likert'
      },
      {
        id: '7',
        question: 'Trouble concentrating on things, such as reading the newspaper or watching television.',
        category: 2,
        type: 'likert'
      },
      {
        id: '8',
        question: 'Moving or speaking so slowly that other people could have noticed; or the opposite – being so fidgety or restless that you have been moving around a lot more than usual.',
        category: 2,
        type: 'likert'
      },
      {
        id: '9',
        question: 'Thoughts that you would be better off dead, or of hurting yourself in some way.',
        category: 2,
        type: 'likert'
      }
    ],
    3: [
      {
        id: '1',
        question: 'Feeling nervous, anxious, or on edge.',
        category: 3,
        type: 'likert'
      },
      {
        id: '2',
        question: 'Not being able to stop or control worrying.',
        category: 3,
        type: 'likert'
      },
      {
        id: '3',
        question: 'Worrying too much about different things.',
        category: 3,
        type: 'likert'
      },
      {
        id: '4',
        question: 'Trouble relaxing.',
        category: 3,
        type: 'likert'
      },
      {
        id: '5',
        question: 'Being so restless that it is hard to sit still.',
        category: 3,
        type: 'likert'
      },
      {
        id: '6',
        question: 'Becoming easily annoyed or irritable.',
        category: 3,
        type: 'likert'
      },
      {
        id: '7',
        question: 'Feeling afraid as if something awful might happen.',
        category: 3,
        type: 'likert'
      }
    ],
    4: [
      {
        id: '1',
        question: 'I feel comfortable expressing my gender identity without fear.',
        category: 4,
        type: 'likert'
      },
      {
        id: '2',
        question: 'I experience stress or conflict when thinking about my gender or orientation.',
        category: 4,
        type: 'likert'
      },
      {
        id: '3',
        question: 'I have access to safe spaces where I can talk about my gender concerns.',
        category: 4,
        type: 'likert'
      },
      {
        id: '4',
        question: 'My mental health has been affected by issues related to my gender or orientation.',
        category: 4,
        type: 'likert'
      }
    ]
  };

  // Get current questions based on selected category
  const getCurrentQuestions = () => {
    if (!selectedCategory) {
      return [categorySelectionQuestion];
    }
    // When category is selected, only return category-specific questions
    return categoryQuestions[selectedCategory as keyof typeof categoryQuestions] || [];
  };

  const questions = getCurrentQuestions();

  // DASS-21 scale options (0-3 scale)
  const getAnswerOptions = () => {
    if (selectedCategory === 2 || selectedCategory === 3) {
      // PHQ-9 and GAD-7 use 0-3 scale
      return [
        { value: 0, label: 'Not at all' },
        { value: 1, label: 'Several days' },
        { value: 2, label: 'More than half the days' },
        { value: 3, label: 'Nearly every day' }
      ];
    } else if (selectedCategory === 4) {
      // Gender Sensitivity Questionnaire uses 1-5 scale
      return [
        { value: 1, label: 'Strongly Disagree' },
        { value: 2, label: 'Disagree' },
        { value: 3, label: 'Neutral' },
        { value: 4, label: 'Agree' },
        { value: 5, label: 'Strongly Agree' }
      ];
    } else {
      // DASS-21 uses 0-3 scale (0 = no symptoms, 3 = severe symptoms)
      return [
        { value: 0, label: 'Did not apply to me at all' },
        { value: 1, label: 'Applied to me to some degree, or some of the time' },
        { value: 2, label: 'Applied to me to a considerable degree, or a good part of the time' },
        { value: 3, label: 'Applied to me very much, or most of the time', forceValue: 3 }
      ];
    }
  };

  const answerOptions = getAnswerOptions();

  const currentQuestionData = questions[currentQuestion - 1];
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;
  const progress = (currentQuestion / totalQuestions) * 100;


  // Type guard to check if question has options (selection type)
  const isSelectionQuestion = (question: any): question is { id: string; question: string; type: 'selection'; options: string[] } => {
    return question.type === 'selection' && 'options' in question;
  };

  // Type guard to check if question has category (likert type)
  const isLikertQuestion = (question: any): question is { id: string; question: string; category: number; type: string } => {
    return question.type === 'likert' && 'category' in question;
  };

  const getCategoryName = (categoryNumber: number) => {
    switch (categoryNumber) {
      case 1: return 'DASS-21 Assessment';
      case 2: return 'PHQ-9 Assessment';
      case 3: return 'GAD-7 Assessment';
      case 4: return 'Gender Sensitivity Assessment';
      default: return 'Unknown Category';
    }
  };

  const saveCurrentResponse = (answer: number) => {
    if (!currentQuestionData) return;
    
    const newResponse: AssessmentResponse = {
      questionId: currentQuestionData.id,
      answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestionData.id);
      const updated = [...filtered, newResponse];
      return updated;
    });

    // If this is the category selection question, set the selected category
    if (currentQuestionData.id === '0') {
      const categoryMap = [1, 2, 3, 4]; // Map selection index to category number
      setSelectedCategory(categoryMap[answer]);
      // Reset to question 1 for the selected category
      setCurrentQuestion(1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      const prevQuestion = currentQuestion - 1;
      setCurrentQuestion(prevQuestion);
    }
  };

  const calculateScores = (responses: AssessmentResponse[], selectedCategory: number) => {
    // Filter out the category selection response (id: '0')
    const assessmentResponses = responses.filter(r => r.questionId !== '0');
    
    if (selectedCategory === 1) {
      // DASS-21 scoring
      const dassResponses = assessmentResponses.filter(r => 
        categoryQuestions[1]?.some(q => q.id === r.questionId)
      );
      
      // Sort responses by question id
      const sortedResponses = dassResponses.sort((a, b) => parseInt(a.questionId) - parseInt(b.questionId));
      
      // Depression: first 7 questions (1-7)
      const depressionResponses = sortedResponses.slice(0, 7);
      // Anxiety: next 7 questions (8-14)
      const anxietyResponses = sortedResponses.slice(7, 14);
      // Stress: last 7 questions (15-21)
      const stressResponses = sortedResponses.slice(14, 21);
      
      // Calculate raw scores (sum of responses)
      const depressionRaw = depressionResponses.reduce((sum, r) => sum + r.answer, 0);
      const anxietyRaw = anxietyResponses.reduce((sum, r) => sum + r.answer, 0);
      const stressRaw = stressResponses.reduce((sum, r) => sum + r.answer, 0);
      
      // Calculate final scores (raw score × 2)
      const depressionScore = depressionRaw * 2;
      const anxietyScore = anxietyRaw * 2;
      const stressScore = stressRaw * 2;
      
      // Overall score is average of the three subscales
      const overallScore = Math.round((depressionScore + anxietyScore + stressScore) / 3);
      
      return {
        overall: overallScore,
        category1: overallScore,
        category2: 0,
        category3: 0,
        category4: 0,
        subscales: {
          depression: depressionScore,
          anxiety: anxietyScore,
          stress: stressScore
        }
      };
    } else if (selectedCategory === 2) {
      // PHQ-9 scoring (sum of all 9 questions, max 27)
      const phqResponses = assessmentResponses.filter(r =>
        categoryQuestions[2]?.some(q => q.id === r.questionId)
      );

      const phqTotal = phqResponses.reduce((sum, r) => sum + r.answer, 0);

      return {
        overall: phqTotal,
        category1: 0,
        category2: phqTotal,
        category3: 0,
        category4: 0,
        phqTotal: phqTotal // Store raw total for interpretation
      };
    } else if (selectedCategory === 3) {
      // GAD-7 scoring (sum of all 7 questions, max 21)
      const gadResponses = assessmentResponses.filter(r =>
        categoryQuestions[3]?.some(q => q.id === r.questionId)
      );

      const gadTotal = gadResponses.reduce((sum, r) => sum + r.answer, 0);

      return {
        overall: gadTotal,
        category1: 0,
        category2: 0,
        category3: gadTotal,
        category4: 0,
        gadTotal: gadTotal // Store raw total for interpretation
      };
    } else if (selectedCategory === 4) {
      // Gender Sensitivity Questionnaire scoring (4 questions, 1-5 scale)
      const genderResponses = assessmentResponses.filter(r =>
        categoryQuestions[4]?.some(q => q.id === r.questionId)
      );

      // Q1 & Q3: direct scoring (Strongly Agree = good)
      // Q2 & Q4: reverse scoring (Strongly Disagree = good)
      const scoredResponses = genderResponses.map(r => {
        const questionNumber = parseInt(r.questionId);
        if (questionNumber === 2 || questionNumber === 4) {
          // Reverse scoring: 1->5, 2->4, 3->3, 4->2, 5->1
          return { ...r, answer: 6 - r.answer };
        }
        // Q1 & Q3: direct
        return r;
      });

      const genderTotal = scoredResponses.reduce((sum, r) => sum + r.answer, 0);

      return {
        overall: genderTotal,
        category1: 0,
        category2: 0,
        category3: 0,
        category4: genderTotal,
        genderTotal: genderTotal // Store raw total for interpretation
      };
    } else {
      // Other categories use original scoring (5 questions * 5 max = 25)
      const categoryResponses = assessmentResponses.filter(r => 
        categoryQuestions[selectedCategory as keyof typeof categoryQuestions]?.some(q => q.id === r.questionId)
      );
      
      const categoryTotal = categoryResponses.reduce((sum, r) => sum + r.answer, 0);
      const categoryScore = Math.round((categoryTotal / 25) * 100);

      return {
        overall: categoryScore,
        category1: selectedCategory === 1 ? categoryScore : 0,
        category2: selectedCategory === 2 ? categoryScore : 0,
        category3: selectedCategory === 3 ? categoryScore : 0,
        category4: selectedCategory === 4 ? categoryScore : 0
      };
    }
  };

    const interpretResults = (scores: any, selectedCategory: number) => {
      if (selectedCategory === 1) {
        // DASS-21 interpretation (inverted scale: higher scores = better mental health)
        const { subscales } = scores;

        const interpretDassScore = (score: number, type: 'depression' | 'anxiety' | 'stress') => {
          let level: string;
          let interpretation: string;

          if (type === 'depression') {
            if (score <= 9) { level = 'Normal'; interpretation = 'No significant depression symptoms'; }
            else if (score <= 13) { level = 'Mild'; interpretation = 'Mild depression symptoms present'; }
            else if (score <= 20) { level = 'Moderate'; interpretation = 'Moderate depression symptoms'; }
            else if (score <= 27) { level = 'Severe'; interpretation = 'Severe depression symptoms'; }
            else { level = 'Extremely Severe'; interpretation = 'Extremely severe depression symptoms'; }
          } else if (type === 'anxiety') {
            if (score <= 7) { level = 'Normal'; interpretation = 'No significant anxiety symptoms'; }
            else if (score <= 9) { level = 'Mild'; interpretation = 'Mild anxiety symptoms present'; }
            else if (score <= 14) { level = 'Moderate'; interpretation = 'Moderate anxiety symptoms'; }
            else if (score <= 19) { level = 'Severe'; interpretation = 'Severe anxiety symptoms'; }
            else { level = 'Extremely Severe'; interpretation = 'Extremely severe anxiety symptoms'; }
          } else { // stress
            if (score <= 14) { level = 'Normal'; interpretation = 'No significant stress symptoms'; }
            else if (score <= 18) { level = 'Mild'; interpretation = 'Mild stress symptoms present'; }
            else if (score <= 25) { level = 'Moderate'; interpretation = 'Moderate stress symptoms'; }
            else if (score <= 33) { level = 'Severe'; interpretation = 'Severe stress symptoms'; }
            else { level = 'Extremely Severe'; interpretation = 'Extremely severe stress symptoms'; }
          }

          return { level, interpretation };
        };

        const depressionResult = interpretDassScore(subscales.depression, 'depression');
        const anxietyResult = interpretDassScore(subscales.anxiety, 'anxiety');
        const stressResult = interpretDassScore(subscales.stress, 'stress');

        // Determine overall level based on highest subscale (higher = worse mental health)
        const maxScore = Math.max(subscales.depression, subscales.anxiety, subscales.stress);
        let overallLevel: 'Low' | 'Moderate' | 'High';

        if (maxScore <= 14) overallLevel = 'Low';     // Best mental health = Low severity
        else if (maxScore <= 25) overallLevel = 'Moderate'; // Moderate mental health = Moderate severity
        else overallLevel = 'High';                     // Poor mental health = High severity
      
        // Create structured interpretation with subscales as an object for proper layout rendering
        const interpretation = {
          subscales: {
            depression: { score: subscales.depression, level: depressionResult.level },
            anxiety: { score: subscales.anxiety, level: anxietyResult.level },
            stress: { score: subscales.stress, level: stressResult.level }
          },
          subscaleInterpretations: {
            depression: depressionResult.interpretation,
            anxiety: anxietyResult.interpretation,
            stress: stressResult.interpretation
          },
          text: [depressionResult.interpretation, anxietyResult.interpretation, stressResult.interpretation].filter(Boolean).join(' ')
        };
        let recommendations: string[] = [];
        if (
          depressionResult.level === 'Normal' &&
          anxietyResult.level === 'Normal' &&
          stressResult.level === 'Normal'
        ) {
          // All subscales normal
          recommendations = [
            'Continue maintaining healthy lifestyle habits, including regular sleep, balanced nutrition, and physical activity.',
            'Practice ongoing stress management techniques such as deep breathing, mindfulness, or meditation.',
            'Stay connected with supportive friends, family, or community groups.',
            'Monitor your mood and stress levels periodically, and seek support early if you notice changes.',
            'Celebrate your current mental well-being and consider sharing positive coping strategies with others.'
          ];
        } else {
          // At least one subscale is Mild or higher
          if (
            depressionResult.level === 'Mild' ||
            anxietyResult.level === 'Mild' ||
            stressResult.level === 'Mild'
          ) {
            recommendations.push(
              'You may be experiencing mild symptoms. Consider talking to a trusted friend, family member, or counselor about your feelings.',
              'Engage in regular physical activity and relaxation techniques to help manage stress and mood.',
              'Maintain a regular sleep schedule and healthy eating habits.',
              'Monitor your symptoms and seek professional support if they persist or worsen.'
            );
          }
          if (
            depressionResult.level === 'Moderate' ||
            anxietyResult.level === 'Moderate' ||
            stressResult.level === 'Moderate'
          ) {
            recommendations.push(
              'Your results indicate moderate symptoms. It is recommended to consult with a mental health professional for further assessment and support.',
              'Practice stress management techniques such as deep breathing, mindfulness, or meditation.',
              'Stay connected with supportive people and avoid social isolation.',
              'Consider journaling or tracking your mood to identify patterns and triggers.'
            );
          }
          if (
            depressionResult.level === 'Severe' ||
            anxietyResult.level === 'Severe' ||
            stressResult.level === 'Severe'
          ) {
            recommendations.push(
              'Severe symptoms detected. Please seek support from a mental health professional as soon as possible.',
              'Reach out to trusted friends or family for immediate support.',
              'Prioritize self-care and avoid major life decisions until you have received professional guidance.',
              'If you feel at risk of harm, contact emergency services or a crisis helpline immediately.'
            );
          }
          if (
            depressionResult.level === 'Extremely Severe' ||
            anxietyResult.level === 'Extremely Severe' ||
            stressResult.level === 'Extremely Severe'
          ) {
            recommendations.push(
              'Extremely severe symptoms detected. This is a mental health emergency. Please seek immediate help from a mental health professional or emergency services.',
              'Do not hesitate to reach out to crisis support resources or helplines.',
              'Ensure you are not alone–ask a trusted person to stay with you if possible.',
              'Your well-being is important. Professional help is available and effective.'
            );
          }
        }
        return { level: overallLevel, interpretation, recommendations };
    } else if (selectedCategory === 2) {
      // PHQ-9 completion: match result score, color, and filled dots for each band
      const { phqTotal } = scores;
      let resultLabel: string;
      let color: 'green' | 'orange' | 'red';
      let filledDots: number;
      let recommendations: string[] = [];
      if (phqTotal <= 4) {
        resultLabel = 'Strong Support System';
        color = 'green';
        filledDots = 1;
        recommendations = [
          'You have a strong support system. Continue maintaining your current lifestyle and support networks.',
          'Stay connected with friends and family for ongoing support.',
          'Keep up with regular physical activity and healthy habits.'
        ];
      } else if (phqTotal <= 9) {
        resultLabel = 'Stable Social Resources';
        color = 'orange';
        filledDots = 2;
        recommendations = [
          'Your social resources are stable, but could benefit from strengthening.',
          'Engage in activities that foster social connections.',
          'Consider talking to a trusted friend or family member about your feelings.'
        ];
      } else if (phqTotal <= 14) {
        resultLabel = 'Vulnerable Support System';
        color = 'orange';
        filledDots = 2;
        recommendations = [
          'Your support system may be vulnerable. Consider seeking additional support.',
          'Increase social support and maintain connections with others.',
          'Practice self-care activities and stress reduction techniques.'
        ];
      } else if (phqTotal <= 19) {
        resultLabel = 'Insufficient Social Support';
        color = 'red';
        filledDots = 3;
        recommendations = [
          'Your social support is insufficient. Seek professional or community support as soon as possible.',
          'Contact your primary care provider to discuss support options.',
          'Engage trusted friends or family members for immediate support.'
        ];
      } else {
        resultLabel = 'Lack of Protective Support';
        color = 'red';
        filledDots = 3;
        recommendations = [
          'There is a lack of protective support. Seek immediate professional or crisis support.',
          'Contact your primary care provider or emergency services if needed.',
          'Reach out to crisis support resources immediately.'
        ];
      }
      return {
        level: color === 'green' ? 'Low' : color === 'orange' ? 'Moderate' : 'High',
        interpretation: resultLabel,
        recommendations,
        color,
        filledDots
      };
    } else if (selectedCategory === 3) {
      // GAD-7 interpretation
      const { gadTotal } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      if (gadTotal <= 4) {
        level = 'Low';
        interpretation = 'Minimal anxiety symptoms. Your responses suggest you are experiencing very few or no significant anxiety symptoms at this time.';
        recommendations = [
          'Continue maintaining your current lifestyle and coping strategies',
          'Practice stress management techniques like deep breathing or meditation',
          'Stay connected with friends and family for ongoing support',
          'Keep up with regular physical activity and healthy habits'
        ];
      } else if (gadTotal <= 9) {
        level = 'Low';
        interpretation = 'Mild anxiety symptoms. You may be experiencing some mild anxiety symptoms that could benefit from attention and support.';
        recommendations = [
          'Consider talking to a trusted friend or family member about your feelings',
          'Practice relaxation techniques like progressive muscle relaxation',
          'Engage in activities that help you feel calm and centered',
          'Maintain regular sleep and exercise routines'
        ];
      } else if (gadTotal <= 14) {
        level = 'Moderate';
        interpretation = 'Moderate anxiety symptoms. Your responses indicate moderate levels of anxiety that would benefit from professional support and intervention.';
        recommendations = [
          'Consider speaking with a mental health professional or counselor',
          'Talk to your primary care provider about your symptoms',
          'Practice mindfulness and anxiety management techniques',
          'Consider cognitive-behavioral strategies for anxiety'
        ];
      } else {
        level = 'High';
        interpretation = 'Severe anxiety symptoms. Your responses suggest significant anxiety symptoms that require professional attention and support.';
        recommendations = [
          'Seek professional mental health support as soon as possible',
          'Contact your primary care provider to discuss treatment options',
          'Practice grounding techniques and anxiety management strategies',
          'Consider reaching out to crisis support resources if needed',
          'Engage trusted friends or family members for immediate support'
        ];
      }
      
      return { level, interpretation, recommendations };
    } else if (selectedCategory === 4) {
      // Gender Sensitivity Questionnaire interpretation
      const { genderTotal } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      if (genderTotal >= 16) {
        level = 'High';
        interpretation = 'High gender sensitivity and self-acceptance. You demonstrate strong internal self-acceptance and resilience regarding your gender identity. This suggests good emotional well-being and social support in this area.';
        recommendations = [
          'Continue maintaining your current positive outlook and support systems',
          'Consider sharing your experiences to help others who may be struggling',
          'Stay connected with supportive communities and resources',
          'Keep practicing self-care and maintaining healthy relationships'
        ];
      } else if (genderTotal >= 12) {
        level = 'Moderate';
        interpretation = 'Moderate gender sensitivity. You show some comfort with your gender identity but may experience occasional stress or concerns. This is common and manageable with appropriate support.';
        recommendations = [
          'Consider talking to trusted friends or family members about your feelings',
          'Explore supportive communities or groups that share similar experiences',
          'Practice self-compassion and positive self-talk',
          'Consider speaking with a counselor who specializes in gender-related concerns if needed'
        ];
      } else {
        level = 'Low';
        interpretation = 'Low gender sensitivity score - this may indicate significant stress, depression risk, or social isolation related to gender identity concerns. Consider additional support and potentially taking the DASS-21 assessment for a more comprehensive mental health evaluation.';
        recommendations = [
          'Consider taking the DASS-21 assessment for a comprehensive mental health evaluation',
          'Seek support from mental health professionals who specialize in gender-related concerns',
          'Connect with supportive communities and peer support groups',
          'Consider reaching out to crisis support resources if you are experiencing severe distress',
          'Focus on building a strong support network of understanding friends and family'
        ];
      }
      
      return { level, interpretation, recommendations };
    } else {
      // Original interpretation for other categories
      const { overall } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];

      if (overall >= 75) {
        level = 'High';
        interpretation = 'You demonstrate excellent integration of emotional awareness, psychological understanding, and rational thinking in your decision-making processes. You show strong self-awareness and balanced approaches to complex situations.';
        recommendations = [
          'Continue developing your emotional intelligence through regular reflection',
          'Share your decision-making strategies with others who might benefit',
          'Consider mentoring others in integrated thinking approaches'
        ];
      } else if (overall >= 50) {
        level = 'Moderate';
        interpretation = 'You show good awareness in some areas but may benefit from developing a more balanced approach to decision-making. There is room for growth in integrating emotional, psychological, and rational factors.';
        recommendations = [
          'Practice mindfulness to improve emotional awareness',
          'Learn about cognitive biases to enhance psychological understanding',
          'Develop systematic approaches to problem-solving',
          'Seek feedback on your decision-making processes'
        ];
      } else {
        level = 'Low';
        interpretation = 'You may benefit from developing stronger skills in emotional awareness, psychological understanding, or rational thinking. Consider focusing on one area at a time to build a foundation for integrated decision-making.';
        recommendations = [
          'Start with emotional awareness exercises and journaling',
          'Learn about basic psychological concepts and cognitive biases',
          'Practice structured decision-making frameworks',
          'Consider working with a coach or counselor to develop these skills'
        ];
      }

      return { level, interpretation, recommendations };
    }
  };

  const handleSubmit = async () => {
    // Ensure the category selection response is present as the first item
    let responsesWithCategory = responses;
    const hasCategorySelection = responses.some(r => r.questionId === '0');
    if (!hasCategorySelection && selectedCategory) {
      responsesWithCategory = [
        {
          questionId: '0',
          answer: 0, // 0 = DASS-21, 1 = PHQ-9, etc. (adjust if needed)
          timestamp: new Date()
        },
        ...responses
      ];
    }

    // Filter out the category selection response for validation
    const assessmentResponses = responsesWithCategory.filter(r => r.questionId !== '0');

    if (assessmentResponses.length < totalQuestions) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Pass full responses (including category selection) to calculateScores
      // calculateScores will filter it internally
      const scores = calculateScores(responsesWithCategory, selectedCategory!);
      const results = interpretResults(scores, selectedCategory!);

      const localResult = {
        id: 'local',
        assessmentType: 'emotio-psycho-rational',
        selectedCategory: selectedCategory,
        scores,
        results,
        createdAt: new Date().toISOString()
      };

      // For DASS-21, convert the structured interpretation object to a string for the backend (database compatibility)
      // while keeping the object format for frontend rendering (via localResult)
      let submissionResults = results;
      if (selectedCategory === 1 && typeof results.interpretation === 'object' && results.interpretation.subscales) {
        const {subscales, text} = results.interpretation;
        const interpretationString = `DASS-21 Subscale Breakdown:\n• Depression: ${subscales.depression.score} (${subscales.depression.level})\n• Anxiety: ${subscales.anxiety.score} (${subscales.anxiety.level})\n• Stress: ${subscales.stress.score} (${subscales.stress.level})\nInterpretation: ${text}`;
        submissionResults = {
          ...results,
          interpretation: interpretationString
        };
      }

      const submissionData = { responses: responsesWithCategory, scores, results: submissionResults, selectedCategory };

      const response = await fetch(API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(submissionData)
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Check for token expiration
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        throw new Error(responseData.error || 'Failed to submit assessment');
      }

      // Notify InsightsPage about the update
      assessmentUpdateNotifier.notifyUpdate('mental-health');

      // Navigate to completion page with the locally calculated data
      navigate('/panel/learn/emotio-psycho-rational-assessment/completion', {
        state: { assessmentResult: localResult }
      });

    } catch (error) {

      let errorMessage = 'Error submitting assessment. Please try again.';
      
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        errorMessage = 'Your session has expired. Please log in again to continue.';
      }
      
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Emotio-Psycho-Rational Assessment</h1>
              <p>Processing your responses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if currentQuestionData exists
  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Emotio-Psycho-Rational Assessment</h1>
              <p>Loading assessment questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel emotio-psycho-rational-panel"
        >
          <div className="assessment-header">
            <h1>Emotio-Psycho-Rational Assessment</h1>
            <p className="assessment-tool-info">
              Comprehensive assessment of emotional awareness, psychological understanding, and rational thinking
            </p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              Question {currentQuestion} of {totalQuestions}
            </span>
          </div>

          <div className="question-section">
            {isSelectionQuestion(currentQuestionData) ? (
              <>
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  Please select the assessment category you would like to take.
                </p>

                <div className="answer-options selection-options">
                  {currentQuestionData.options.map((option: string, index: number) => (
                    <button
                      key={index}
                      className={`option-button selection-button ${currentResponse?.answer === index ? 'selected' : ''}`}
                      onClick={() => saveCurrentResponse(index)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </>
            ) : isLikertQuestion(currentQuestionData) ? (
              <>
                <div className="category-indicator">
                  <span className="category-badge">
                    {getCategoryName(currentQuestionData.category)}
                  </span>
                </div>
                
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  {selectedCategory === 3 
                    ? 'Please rate how well this statement describes you over the last two weeks using the scale below.'
                    : 'Please rate how well this statement describes you over the past week using the scale below.'}
                </p>

                <div className="answer-options">
                  {answerOptions.map((option) => (
                    <label key={option.value} className="answer-option">
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={option.value}
                        checked={currentResponse?.answer === option.value}
                        onChange={(e) => {
                          // Always force value 3 for 'Applied to me very much, or most of the time'
                          let val = parseInt(e.target.value);
                          if (option.label === 'Applied to me very much, or most of the time') {
                            val = 3;
                          }
                          saveCurrentResponse(val);
                        }}
                      />
                      <span className="option-text">{option.label}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          {!isSelectionQuestion(currentQuestionData) && (
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={() => {
                  if (assessmentFilter === 'dass21' && currentQuestion === 1) {
                    navigate('/panel/learn/');
                    return;
                  }
                  if (currentQuestion === 1 && selectedCategory === null) {
                    navigate('/panel/learn/');
                    return;
                  }
                  if (currentQuestion === 1 && selectedCategory !== null) {
                    setSelectedCategory(null);
                    setCurrentQuestion(1);
                    return;
                  }
                  handleBack();
                }}
                disabled={false}
              >
                Back
              </button>
              
              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={!currentResponse}
              >
                {currentQuestion === totalQuestions && selectedCategory !== null ? 'Complete Assessment' : 'Next'}
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default EmotioPsychoRationalAssessmentFormPage;


